import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useIsFetching } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { useField } from "formik"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { useResourceBookingDraft } from "../ResourceBookingDraftContext"
import { DateSelectArg, EventClickArg, EventDropArg, EventHoveringArg } from "@fullcalendar/core"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { ConfigQuery } from "../../../createOrderApi"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import {
    ResourceBase,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
} from "../calendar/fullCalendarTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { AbandonBookingDialog } from "../../AbandonBookingDialog"
import { forceFullRevert, toFloatingIsoMinutesFromJsDate } from "../calendar/helpers"

import { BookPeriodMessageDialog } from "./BookPeriodMessageDialog"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import {
    BOOKING_ROUND_MINUTES,
    createNewEvent,
    DEFAULT_MINIMUM_DURATION_MINUTES,
    isCanEditExistOrder,
    isLocalEvent,
    tryRoundEndTime,
    tryRoundStartTime,
} from "./helpers"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { HoverTooltipLayer, ResourceBookingCalendarHoveredEvent } from "./tooltip/HoverTooltipLayer"
import { useResourceHelpers } from "./timelineHooks"
import { useRefreshCalendar } from "../context/RefreshCalendarProvider"
import { ResourceLabel } from "./ResourceLabel"
import { useResourceLabelSharedContext } from "../context/ResourceLabelContext"
import { useTimelineContext } from "../context/TimelineContextProvider"
import { TimeChangeDialog } from "./TimeChangeDialog"
import { useTimeChangeDialog } from "./useTimeChangeDialog"

const DAY_OFFSETS = [0, 1, 2] as const

type ResourceBookingCalendarProps = Readonly<{
    groupImagesMap: Map<string, string>
    handleChangeOrderContext: (orderId: string) => void
}>

export const ResourceBookingCalendar = memo(function ResourceBookingCalendar({
    groupImagesMap,
    handleChangeOrderContext,
}: ResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const isDraggingRef = useRef(false)
    const setHoverRef = useRef<(event: ResourceBookingCalendarHoveredEvent | null) => void>(() => {})

    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const [showBookPeriodDialogMessage, setShowBookPeriodDialogMessage] = useState(false)
    const [confirmAbandonDialogState, setConfirmAbandonDialogState] = useState<{ id: string; title: string } | null>(
        null,
    )
    const { setCalendarRef } = useRefreshCalendar()
    const { loadingServiceCenterIds } = useResourceLabelSharedContext()
    const {
        timelineResources,
        resourceBufferMap,
        allServiceCenterIdsForEvents,
        userId,
        localEventUpdateId,
        isBookPeriodMode,
        isOutlookFlow,
    } = useTimelineContext()
    const { draftEvent: localEvent } = useResourceBookingDraft()
    const { createSingleDayFetcher, handleChangeLocalEvent, isResourceAvailable } = useResourceHelpers({})
    const { timeChangeDialogState, setTimeChangeDialogState, handleKeepOriginalTime, handleConfirmTimeChange } =
        useTimeChangeDialog({ handleChangeLocalEvent, isResourceAvailable })
    const [{ value: selectedDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const activeFetches = useIsFetching({
        queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY, userId],
    })
    const isLoadingEvents = activeFetches > 0

    const localEventsSource = useMemo(
        () => ({
            id: "local-events",
            events: localEvent ? [localEvent] : [],
        }),
        [localEvent],
    )

    const serverEventsSource = useMemo(
        () =>
            allServiceCenterIdsForEvents.flatMap((id) =>
                DAY_OFFSETS.map((dayOffset) => ({
                    id: `source-${id}-day${dayOffset}`,
                    events: createSingleDayFetcher(id, dayOffset),
                })),
            ),
        [allServiceCenterIdsForEvents, createSingleDayFetcher],
    )

    const eventSources = useMemo(() => {
        return [localEventsSource, ...serverEventsSource]
    }, [serverEventsSource, localEventsSource])

    const handleSelect = ({ startStr, endStr, resource }: DateSelectArg) => {
        if (!resource) return

        if (isBookPeriodMode) {
            setShowBookPeriodDialogMessage(true)
            return
        }

        const startDt = dateTimeFactory.fromISO(startStr)
        const endDtRaw = dateTimeFactory.fromISO(endStr)
        const durationMinutes = endDtRaw.diff(startDt, "minutes").minutes

        // FullCalendar uses `snapDuration` as the selection length on simple click.
        // With fine-grained snapping (e.g. 1 minute), this would otherwise create 1-minute events.
        const endDtBase =
            Number.isFinite(durationMinutes) && durationMinutes <= 1
                ? startDt.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })
                : endDtRaw

        const resourceId = resource.id
        const isAvailableForResource = (s: string, e: string) => isResourceAvailable(s, e, [resourceId], localEvent?.id)

        // Round start down / end up to the nearest 5-minute boundary,
        // but only when the rounded slot doesn't overlap another booked event (including buffers).
        const startDtRounded = tryRoundStartTime({
            start: startDt,
            end: endDtBase,
            roundToMinutes: BOOKING_ROUND_MINUTES,
            isAvailable: isAvailableForResource,
            toISO: (dt) => dateTimeFactory.toApiISOMinutesFormat(dt),
        })
        const endDt = tryRoundEndTime({
            start: startDtRounded,
            end: endDtBase,
            roundToMinutes: BOOKING_ROUND_MINUTES,
            isAvailable: isAvailableForResource,
            toISO: (dt) => dateTimeFactory.toApiISOMinutesFormat(dt),
        })

        const start = dateTimeFactory.toApiISOMinutesFormat(startDtRounded)
        const end = dateTimeFactory.toApiISOMinutesFormat(endDt)

        const currentResources = localEvent?.resourceIds || []

        const hasOutlookTimeGuard = isOutlookFlow && localEvent && currentResources.length === 0
        if (hasOutlookTimeGuard) {
            const isTimeDifferent = localEvent.start !== start || localEvent.end !== end

            if (isTimeDifferent) {
                const updatedResourceIds = [resourceId]

                const isFreeAtNewTime = isResourceAvailable(start, end, updatedResourceIds, localEvent.id)

                if (!isFreeAtNewTime) {
                    enqueueSnackbar({
                        variant: "warning",
                        message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                    })
                    return
                }

                setTimeChangeDialogState({
                    resourceId,
                    newStart: start,
                    newEnd: end,
                })
                return
            }
        }

        if (localEvent) {
            const updatedResourceIds = currentResources.includes(resourceId)
                ? currentResources
                : [...currentResources, resourceId]

            if (!isResourceAvailable(start, end, updatedResourceIds, localEvent.id)) {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-error-select"),
                })
                return
            }

            if (currentResources.includes(resourceId)) {
                handleChangeLocalEvent({
                    ...localEvent,
                    start,
                    end,
                })

                return
            } else {
                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: [...currentResources, resourceId],
                    start,
                    end,
                })

                return
            }
        }

        if (!isResourceAvailable(start, end, [resourceId])) {
            return
        }

        handleChangeLocalEvent(createNewEvent(resourceId, start, end))
    }

    const handleEventDrop = (info: EventDropArg) => {
        if (isLoadingEvents) {
            forceFullRevert(info)
            return
        }
        const { event, oldResource } = info
        const { id, extendedProps, start, end } = event

        if (isBookPeriodMode && isLocalEvent(id)) {
            setShowBookPeriodDialogMessage(true)
            forceFullRevert(info)
            return
        }

        if (!isLocalEvent(id)) {
            if (isCanEditExistOrder(info)) {
                if (localEvent) {
                    setConfirmAbandonDialogState({ id, title: extendedProps["subject"] })
                } else handleChangeOrderContext(id)
            } else {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-permission-error"),
                })
            }
            forceFullRevert(info)
            return
        }

        const newResourceIds = event.getResources().map((res) => res.id)
        const newStart = toFloatingIsoMinutesFromJsDate(dateTimeFactory, start!)
        const newEnd = toFloatingIsoMinutesFromJsDate(dateTimeFactory, end!)

        if (!isResourceAvailable(newStart, newEnd, newResourceIds, id)) {
            forceFullRevert(info)
            return
        }

        const isAvailableForDrop = (s: string, e: string) => isResourceAvailable(s, e, newResourceIds, id)
        const newStartDt = dateTimeFactory.fromISO(newStart)
        const newEndDt = dateTimeFactory.fromISO(newEnd)

        const roundedStartDt = tryRoundStartTime({
            start: newStartDt,
            end: newEndDt,
            roundToMinutes: BOOKING_ROUND_MINUTES,
            isAvailable: isAvailableForDrop,
            toISO: (dt) => dateTimeFactory.toApiISOMinutesFormat(dt),
        })
        const roundedEndDt = tryRoundEndTime({
            start: roundedStartDt,
            end: newEndDt,
            roundToMinutes: BOOKING_ROUND_MINUTES,
            isAvailable: isAvailableForDrop,
            toISO: (dt) => dateTimeFactory.toApiISOMinutesFormat(dt),
        })
        const roundedStart = dateTimeFactory.toApiISOMinutesFormat(roundedStartDt)
        const roundedEnd = dateTimeFactory.toApiISOMinutesFormat(roundedEndDt)

        if (localEvent) {
            const currentResourceIds = localEvent.resourceIds
            const draggedResourceId = oldResource?.id

            const updatedResourceIds = draggedResourceId
                ? currentResourceIds
                      .filter((id) => id !== draggedResourceId)
                      .concat(newResourceIds.filter((id) => !currentResourceIds.includes(id)))
                : [...new Set([...currentResourceIds, ...newResourceIds])]

            const updatedEvent = {
                ...localEvent,
                resourceIds: updatedResourceIds,
                start: roundedStart,
                end: roundedEnd,
            }

            handleChangeLocalEvent(updatedEvent)
        }
    }

    const handleEventClick = (info: EventClickArg) => {
        const eventId = info.event.id
        const clickedElement = info.jsEvent.target as HTMLElement
        const clickedResourceId = clickedElement?.closest("[data-resource-id]")?.getAttribute("data-resource-id")

        if (!clickedResourceId) {
            return
        }

        if (localEvent && localEvent.id === eventId) {
            const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== clickedResourceId)

            if (updatedResourceIds.length === 0) {
                if (isOutlookFlow) {
                    handleChangeLocalEvent({ ...localEvent, resourceIds: [] })
                } else {
                    handleChangeLocalEvent(null)
                }
            } else {
                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: updatedResourceIds,
                })
            }
        }
    }

    const handleEventResize = (info: EventResizeDoneArg) => {
        const { id, start, end, extendedProps } = info.event

        if (isBookPeriodMode && isLocalEvent(id)) {
            setShowBookPeriodDialogMessage(true)
            forceFullRevert(info)
            return
        }
        if (!isLocalEvent(id)) {
            if (isCanEditExistOrder(info)) {
                if (localEvent) {
                    setConfirmAbandonDialogState({ id, title: extendedProps["subject"] })
                } else handleChangeOrderContext(id)
            } else {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-permission-error"),
                })
            }
            forceFullRevert(info)
            return
        }

        const newStart = toFloatingIsoMinutesFromJsDate(dateTimeFactory, start!)
        const newEnd = toFloatingIsoMinutesFromJsDate(dateTimeFactory, end!)

        const resourceIds = info.event.getResources().map((res) => res.id)

        if (!isResourceAvailable(newStart, newEnd, resourceIds, id)) {
            forceFullRevert(info)
            return
        }

        const roundedEndDt = tryRoundEndTime({
            start: dateTimeFactory.fromISO(newStart),
            end: dateTimeFactory.fromISO(newEnd),
            roundToMinutes: BOOKING_ROUND_MINUTES,
            isAvailable: (s, e) => isResourceAvailable(s, e, resourceIds, id),
            toISO: (dt) => dateTimeFactory.toApiISOMinutesFormat(dt),
        })

        handleChangeLocalEvent({
            ...localEvent!,
            start: newStart,
            end: dateTimeFactory.toApiISOMinutesFormat(roundedEndDt),
        })
    }

    const resourceLabelContent = useCallback(
        ({ resource }: TimelineResourceLabelContent) => <ResourceLabel resource={resource} />,
        [],
    )

    const resourceGroupLabelContent = useCallback(
        ({ groupValue }: TimelineResourceGroupLabelContent) => (
            <ResourceCalendarGroupContent
                title={groupValue}
                groupImagesMap={groupImagesMap}
                onImageClick={openImageModal}
            />
        ),
        [groupImagesMap, openImageModal],
    )

    /** Same service-center key as `EVENTS_LIST_BY_DAY` query key and ResourceLabel loading state. */
    const isResourceLaneLoading = useCallback(
        (extendedProps: ResourceBase) => loadingServiceCenterIds.has(extendedProps.locationId.split("_")[0] ?? ""),
        [loadingServiceCenterIds],
    )

    const handleEventHover = useCallback(
        (info: EventHoveringArg | null) => {
            if (isDraggingRef.current) return
            if (!info || !info.el) {
                setHoverRef.current(null)
                return
            }

            const { el } = info
            const { id, start, end, extendedProps } = info.event

            const rect = el.getBoundingClientRect()

            const top = rect.top + window.scrollY - 8
            const left = rect.left + window.scrollX + rect.width / 2

            const checkEvent = isLocalEvent(id)
            setHoverRef.current({
                id,
                top,
                left,
                local: checkEvent,
                start: dateTimeFactory.formatDateTime(dateTimeFactory.fromJSDate(start!)),
                end: dateTimeFactory.formatDateTime(dateTimeFactory.fromJSDate(end!)),
                host: checkEvent ? undefined : extendedProps["host"],
                subject: checkEvent ? undefined : extendedProps["subject"],
            })
        },
        [dateTimeFactory],
    )

    const handleCloseConfirmAbandonDialog = () => setConfirmAbandonDialogState(null)
    const handleConfirmAbandonDialogClick = () => {
        confirmAbandonDialogState && handleChangeOrderContext(confirmAbandonDialogState.id)
        handleCloseConfirmAbandonDialog()
    }

    return (
        <>
            <StandardTimelineCalendar
                ref={setCalendarRef}
                viewMode="daysGrid"
                localEvents={localEventsSource?.events}
                eventSources={eventSources}
                isLoadingEvents={isLoadingEvents}
                resources={timelineResources}
                resourceBufferMap={resourceBufferMap}
                handleSelect={handleSelect}
                handleEventDrop={handleEventDrop}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                isResourceLaneLoading={isResourceLaneLoading}
                handleEventClick={handleEventClick}
                handleEventResize={handleEventResize}
                handleEventHover={handleEventHover}
                isBookedPeriod={isBookPeriodMode}
                isResourceAvailable={isResourceAvailable}
                selectedDeliveries={selectedDeliveries}
                isDraggingRef={isDraggingRef}
                isUpdateMode={!!localEventUpdateId}
            />

            <HoverTooltipLayer isDraggingRef={isDraggingRef} setHoverRef={setHoverRef} />

            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
            {showBookPeriodDialogMessage && (
                <BookPeriodMessageDialog onClose={() => setShowBookPeriodDialogMessage(false)} />
            )}
            {confirmAbandonDialogState && (
                <LocalizedStrict
                    id={
                        localEventUpdateId
                            ? "create-order-change-current-event-dialog"
                            : "create-order-change-current-event-create-dialog"
                    }
                    attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                    vars={{ orderDetails: `#${confirmAbandonDialogState.id}, ${confirmAbandonDialogState.title}` }}
                >
                    <AbandonBookingDialog
                        title="Confirm booking"
                        message="Are you sure you want to reset changes and update another booking?"
                        handleSubmitClick={handleConfirmAbandonDialogClick}
                        handleCancelClick={handleCloseConfirmAbandonDialog}
                        buttonLabelSubmit="Confirm"
                        buttonLabelCancel="Cancel"
                        open={!!confirmAbandonDialogState}
                        onClose={handleCloseConfirmAbandonDialog}
                    />
                </LocalizedStrict>
            )}
            {timeChangeDialogState && (
                <TimeChangeDialog
                    state={timeChangeDialogState}
                    localEvent={localEvent}
                    onKeepOriginalTime={handleKeepOriginalTime}
                    onConfirmTimeChange={handleConfirmTimeChange}
                />
            )}
        </>
    )
})
